import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'reactstrap';
import serialize from 'form-serialize';
import moment from 'moment';
import FlexGroup from 'components/FlexGroup';
import ErrorMessage from 'components/ErrorMessage';
import { Refresh } from 'components/Pagination';
import NavigationBar from 'components/NavigationBar';
import { Translate, I18n } from 'react-redux-i18n';
import FilterResult from 'components/FilterShortcut/FilterResult';
import AuthView from 'components/AuthView';
import AuthFeature from 'components/AuthFeature';
import Input from 'components/Form/Input';
import FormGroup from 'components/Form/FormGroup';
import cloneDeep from 'lodash/cloneDeep';
import Form from 'components/Form';
import {
    TYPE_RADIO,
    TYPE_SELECT,
    TYPE_TEXT,
    TYPE_TEXTAREA,
} from 'components/Form/Input/input-type';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { updateDocumentTitle, toggleErrorDialog } from 'actions';
import { NAME, TYPE, PIN,DESCRIPTION, CRITERIA } from 'view/FilterManagement/NewFilter';
import {
    useGetFilterDetailQuery,
    useUpdateFilterDetailMutation,
    useGetAuthFilterTypeQuery,
} from 'redux/hook/useFilter';
import { getPinnedOptions } from 'view/FilterManagement/FilterPinStatus';
import { AUTH_EDIT_FILTER } from 'constants/permission';
import FilterCriteria from 'view/FilterManagement/FilterCriteria';

import './filter-detail.scss';

const FilterDetail = () => {
    const i18n = useSelector(state => state.i18n);
    const dispatch = useDispatch();
    const { filterId } = useParams();
    const {
        data: filterDetail,
        error: filterDetailError,
        refetch: getFilterDetail,
    } = useGetFilterDetailQuery(filterId);
    const detailCriteria = useRef();
    const [updateFilterDetail] = useUpdateFilterDetailMutation();
    const [authFilterTypeOpts, setAuthFilterTypeOpts] = useState([]);
    const [, { data : authFilterTypeList } = {}] = useGetAuthFilterTypeQuery();

    const formRef = useRef();
    const [isEdit, setIsEdit] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [name, setName] = useState('');
    const [isPinned, setIsPinned] = useState(false);
    const [description, setDescription] = useState('');
    const [type, setType] = useState();
    const criteria = useRef();


    useEffect(() => {
        dispatch(updateDocumentTitle('filters.filter_detail_title'));
    }, [dispatch, i18n]);

    useEffect(() => {
        let list = [];
        if (authFilterTypeList?.length) {
            list = authFilterTypeList.map(({ type }) => ({
                name: I18n.t(`filters.features_map.${ type }`),
                value: type,
            }));
            list.unshift({ name: '', value: '' });
        }
        setAuthFilterTypeOpts(list);
    }, [authFilterTypeList]);


    const handleFormChange = e => {
        const { name, value } = e.target;
        switch(name) {

            case NAME:
                setName(value);
                break;
            case PIN:
                setIsPinned((value === 'false') ? false : true);
                break;
            case DESCRIPTION:
                setDescription(value);
                break;
            case TYPE:
                if (value === '') {
                    setType();
                }
                else {
                    setType(Number(value));
                }
                criteria.current = {};
                detailCriteria.current = {};
                break;
            default:
        }

    };

    const handleEditUpdate = e => {
         e.preventDefault();
        if (submitLoading) return;
        setSubmitLoading(true);
    };

    const submitFilterDetailUpd = useCallback(async() => {

        const formData = serialize(formRef.current, { hash: true });
        const isPinned = (formData[PIN] === 'false') ? false : true;
        const filterData = {
            id: filterId,
            [NAME]: formData[NAME],
            [TYPE]: Number(formData[TYPE]),
            [PIN]: isPinned,
            [DESCRIPTION]: formData[DESCRIPTION],
            [CRITERIA]: criteria.current,
        };
        try {
            const { error } = await updateFilterDetail({
                ...filterData,
            });
            if (error) {
                throw error;
            }
            else {
                setIsEdit(false);
                await getFilterDetail(filterId);
                setSubmitLoading(false);
            }
        }
        catch (error) {
            const { code } = error?.data || {};

            const message = (code !== undefined && code > 0) ? I18n.t(`filters.create_upd_error_${ code }`) : I18n.t('general_error_message');

            dispatch(toggleErrorDialog(message, () => {
                setSubmitLoading(false);
            }));
        }

    }, [dispatch, updateFilterDetail, filterId, getFilterDetail]);

    const handleEditFilter = e => {
        setIsEdit(true);
    };
    const handleCancelEdit = e => {
        setIsEdit(false);
        handleViewModeDetail();

    };
    const handleViewModeDetail = useCallback(() => {
        const detail = cloneDeep(filterDetail);
        setName(detail?.name || '');
        setIsPinned(detail?.pin);
        setDescription(detail?.description || '');
        setType(detail?.type);
        detailCriteria.current = {
            ...detail?.criteria ?? {},
            invalid_criteria: detail?.invalid_criteria || undefined,
        }

    }, [filterDetail]);

    useEffect(() => {
        if (!isEdit && filterId === filterDetail?.id) {
            handleViewModeDetail();
        }
    }, [isEdit, filterId, filterDetail, handleViewModeDetail]);

    useEffect(()=> {
        if (submitLoading) {
            submitFilterDetailUpd();
        }
    }, [submitLoading, submitFilterDetailUpd]);
    return (
        <AuthView className="filter-detail">
            {
                filterDetailError ? (<ErrorMessage />) : (
                    <Form
                        innerRef={ formRef }
                        className="detail-form"
                        inline
                        onSubmit={ handleEditUpdate }
                        onChange={ handleFormChange }
                        inProgress={ submitLoading }
                        stopSubmitByEnter
                    >
                        <NavigationBar title={ filterId } sticky>
                            <FlexGroup className="detail-functions">
                                {
                                    isEdit ? (
                                        <>
                                            <Button
                                                type="button"
                                                className="btn-cancel"
                                                onClick={ handleCancelEdit }
                                                outline
                                                stopTriggerByTab
                                                disabled={ submitLoading }
                                            >
                                                <Translate value="cancel" />
                                            </Button>
                                            <Button
                                                color="primary"
                                                type="submit"
                                                className="btn-save"
                                                stopTriggerByTab
                                                disabled={ submitLoading }
                                            >
                                                <Translate value="save" />
                                            </Button>
                                        </>
                                    ) : (
                                        <AuthFeature requiredList={ [AUTH_EDIT_FILTER] }>
                                            {
                                                <Button
                                                    type="button"
                                                    color="primary"
                                                    className="btn-edit-fee"
                                                    onClick={ handleEditFilter }
                                                    stopTriggerByTab
                                                >
                                                    <Translate value="edit" />
                                                </Button>
                                            }
                                        </AuthFeature>
                                    )
                                }
                            </FlexGroup>
                        </NavigationBar>
                        <section className="detail-block">
                            <FlexGroup end>
                                {
                                    isEdit ? null : (
                                        <Refresh
                                            time={ moment(filterDetail?.updatedTime) }
                                            onClick={ () => { getFilterDetail(filterId) } }
                                        />
                                    )
                                }
                            </FlexGroup>
                        </section>
                        <FlexGroup className="filter-form-details">
                            <FormGroup title={ I18n.t('filters.details') } className="filter-config-block">
                                <Input
                                    name={ NAME }
                                    type={ TYPE_TEXT }
                                    caption="filters.name"
                                    value={ name }
                                    maxLength={ 50 }
                                    required
                                    viewMode={ !isEdit }
                                />
                                <FlexGroup className="filter-pin-group">
                                    <Input
                                        name={ PIN }
                                        type={ TYPE_RADIO }
                                        caption="filters.filter_pinned"
                                        value={ getPinnedOptions() }
                                        selected={ isPinned }
                                        required
                                        viewMode={ !isEdit }
                                    />
                                </FlexGroup>
                                <FlexGroup className="description-wrapper">
                                    <Input
                                        className="description"
                                        name={ DESCRIPTION }
                                        type={ TYPE_TEXTAREA }
                                        rows={ 4 }
                                        value={ description }
                                        caption="filters.description"
                                        viewMode={ !isEdit }
                                    />
                                </FlexGroup>
                                <Input
                                    name={ TYPE }
                                    type={ TYPE_SELECT }
                                    caption="filters.filter_feature"
                                    value={ authFilterTypeOpts }
                                    selected={ type }
                                    required
                                    viewMode={ !isEdit }
                                />
                                {
                                    isEdit ? null : (
                                        <FlexGroup className="filter-result-wrapper">
                                            <Input
                                                type={ TYPE_TEXT }
                                                name="filter_result"
                                                caption="filters.result"
                                                unitCaption={
                                                    <FilterResult
                                                        type={ filterDetail?.type }
                                                        payload={ filterDetail?.criteria ? filterDetail.criteria : null }
                                                        context={ I18n.t('filters.result') }
                                                        disabled={ filterDetail?.filterDetail || isEdit || !filterDetail?.has_permission }
                                                        className="btn btn-outline-secondary btn-result"
                                                    />
                                                }
                                                viewMode
                                            />
                                        </FlexGroup>
                                    )
                                }

                            </FormGroup>
                        </FlexGroup>
                        <FilterCriteria
                            feature={ type }
                            criteria={ criteria }
                            defaultSelected={ detailCriteria.current }
                            viewMode={ !isEdit }
                        />
                    </Form>
                )
            }
        </AuthView>
    );
};
export default FilterDetail;
